import { useState, useMemo, useCallback, useEffect } from 'react';

const usePlayerActions = (gameState) => {
  const [pendingInvestments, setPendingInvestments] = useState([]);
  const [mapActions, setMapActions] = useState([]);
  const [selectedCountryPopin, setSelectedCountryPopin] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const totalActions = useMemo(() => pendingInvestments.length + mapActions.length, [pendingInvestments, mapActions]);
  const canPerformAction = useMemo(() => totalActions < 3, [totalActions]);
  const canValidate = useMemo(() => totalActions >= 3, [totalActions]);

  const updateActionMessage = useCallback(() => {
    const currentTotalActions = pendingInvestments.length + mapActions.length;
    if (currentTotalActions === 0) {
      setActionMessage(null);
      return;
    }

    if (currentTotalActions >= 3) {
      setActionMessage({
        text: "Vous avez utilisé toutes vos actions. Prêt à valider ?",
        actions: [...pendingInvestments, ...mapActions],
        validate: true,
      });
    } else {
      const remaining = 3 - currentTotalActions;
      const message = `Il vous reste ${remaining} action${remaining > 1 ? 's' : ''}.`;
      setActionMessage({
        text: message,
        actions: [...pendingInvestments, ...mapActions],
        validate: false,
      });
    }
  }, [pendingInvestments, mapActions]);

  useEffect(() => {
    updateActionMessage();
  }, [pendingInvestments, mapActions, updateActionMessage]);

  const handleInvest = useCallback((stat) => {
    setPendingInvestments(prev => {
      if (prev.includes(stat)) {
        return prev.filter(item => item !== stat);
      } else if (canPerformAction) {
        return [...prev, stat];
      }
      return prev;
    });
  }, [canPerformAction]);

  const addMapAction = useCallback((action, country) => {
    if (!canPerformAction) return;
    setMapActions(currentActions => {
      if (currentActions.some(a => a.country === country)) {
        return currentActions;
      }
      return [...currentActions, { action, country }];
    });
  }, [canPerformAction]);

  const removeMapAction = useCallback((country) => {
    setMapActions(currentActions => currentActions.filter(a => a.country !== country));
  }, []);

  const handleOpenPopin = useCallback((countryName) => {
    // Early return if gameState or required properties are not available
    if (!gameState?.countries_dict || !gameState?.joueur?.short_name) {
      return;
    }

    const countries = Object.values(gameState.countries_dict);
    const country = countries?.find(c => c.short_name === countryName.countryShortName);
    if (!country) return;

    let status = "neutre";
    if (country.pnj === 0) {
      status = "joueur";
    } else if (country.enemies && country.enemies.includes(gameState.joueur.short_name)) {
      status = "ennemi";
    } else if (country.allies && country.allies.includes(gameState.joueur.short_name)) {
      status = "allies";
    }

    const currentAction = mapActions.find(a => a.country === country.short_name);
    setSelectedCountryPopin({
      data: country,
      countryShortName: country.short_name,
      currentActionHex: currentAction?.action,
      status: status
    });
  }, [gameState?.countries_dict, gameState?.joueur?.short_name, mapActions]);

  const handleClosePopin = () => {
    setSelectedCountryPopin(null);
  };

  const resetActions = useCallback(() => {
      setPendingInvestments([]);
      setMapActions([]);
      setActionMessage(null);
  }, []);

  return {
    pendingInvestments,
    mapActions,
    selectedCountryPopin,
    canPerformAction,
    canValidate,
    handleInvest,
    addMapAction,
    removeMapAction,
    handleOpenPopin,
    handleClosePopin,
    resetActions,
    actionMessage,
    setActionMessage
  };
};

export default usePlayerActions;